import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';

const ThemeContext = createContext();

const lightPalette = {
  mode: 'light',
  primary:   { main: '#1976d2' },
  secondary: { main: '#388e3c' },
  background: { default: '#f5f5f5', paper: '#ffffff' },
  text:       { primary: '#212121', secondary: '#616161' },
};

const darkPalette = {
  mode: 'dark',
  primary:   { main: '#90caf9' },
  secondary: { main: '#66bb6a' },
  background: { default: '#121212', paper: '#1e1e1e' },
  text:       { primary: '#e0e0e0', secondary: '#9e9e9e' },
};

function getSystemPreference() {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    const stored = localStorage.getItem('theme');
    return stored || getSystemPreference();
  });

  useEffect(() => {
    localStorage.setItem('theme', mode);
  }, [mode]);

  // Listen for OS-level changes only when no explicit choice has been made
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      if (!localStorage.getItem('theme')) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggleTheme = () => setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));

  const theme = useMemo(
    () => createTheme({
      palette: mode === 'dark' ? darkPalette : lightPalette,
      typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      },
      shape: { borderRadius: 8 },
    }),
    [mode],
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useThemeMode() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeProvider');
  return ctx;
}
